import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Clock, Coins, MapPin, ShoppingCart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

/**
 * ExpiredMarketPage — second-life marketplace for videos whose owners
 * let them lapse. Anyone can browse; signed-in users can buy. Producers
 * receive nothing from a sale here (per spec) so the prices stay
 * intentionally low.
 *
 * Layout:
 *   - Top: optional alerts banner ("3 videos you once liked are now
 *     up for sale") — only renders when the signed-in user has
 *     unread marketplace_alerts.
 *   - Grid of listings, each with thumbnail, location, price, days-
 *     remaining-on-market, and a Buy button.
 */
const ExpiredMarketPage = () => {
  const { user, refreshUser } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [buying, setBuying] = useState(null);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/marketplace/expired?limit=60');
      setListings(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load the expired marketplace");
      setListings([]);
    }
    setLoading(false);
  }, []);

  const fetchAlerts = useCallback(async () => {
    if (!user) { setAlerts([]); return; }
    try {
      const res = await api.get('/marketplace/expired/alerts?limit=30');
      setAlerts(Array.isArray(res.data) ? res.data : []);
    } catch {
      setAlerts([]);
    }
  }, [user]);

  useEffect(() => { fetchListings(); }, [fetchListings]);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const dismissAlerts = async () => {
    try {
      await api.post('/marketplace/expired/alerts/mark-read', {});
      setAlerts([]);
      // Notify the Header so its bell badge clears without waiting for
      // the next 30s poll.
      window.dispatchEvent(new Event('expired-alerts-read'));
    } catch (err) {
      toast.error("Couldn't clear alerts");
    }
  };

  const handleBuy = async (listing) => {
    if (!user) {
      toast.error("Sign in to buy from the marketplace");
      return;
    }
    if (!window.confirm(`Buy "${listing.title}" for ${listing.price_credits.toFixed(2)} credits?\n\nYou'll own the video for 30 days. The original producer (@${listing.producer_username}) gets nothing — they let it lapse.`)) {
      return;
    }
    setBuying(listing.id);
    try {
      const res = await api.post(`/marketplace/expired/${listing.id}/buy`, {});
      toast.success(`Owned! New expiry ${new Date(res.data.new_expires_at).toLocaleDateString()}`);
      // Remove from the displayed list and refresh the user's balance.
      setListings(prev => prev.filter(v => v.id !== listing.id));
      refreshUser?.();
      fetchAlerts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't buy this video");
    }
    setBuying(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
              <ShoppingCart className="h-7 w-7 text-amber-400" /> Expired Marketplace
            </h1>
            <p className="text-gray-400 mt-1 text-sm max-w-2xl">
              Videos whose creators let them lapse. Cheap to claim — once owned,
              you get a fresh 30-day window and the clip lives on your channel.
              The original producer earns nothing from these sales.
            </p>
          </div>
          <Link
            to="/marketplace"
            className="text-xs text-emerald-400 hover:text-emerald-300 underline self-start mt-2"
            data-testid="back-to-marketplace"
          >
            ← Back to the main Marketplace
          </Link>
        </header>

        {alerts.length > 0 && (
          <div
            className="mb-6 p-4 rounded-xl border border-cyan-500/40 bg-cyan-500/10"
            data-testid="expired-market-alerts-banner"
          >
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-2">
                <Bell className="h-5 w-5 text-cyan-300 mt-0.5" />
                <div>
                  <h2 className="font-semibold text-cyan-100">
                    {alerts.length} {alerts.length === 1 ? 'clip you once liked is' : 'clips you once liked are'} now for sale
                  </h2>
                  <p className="text-xs text-cyan-200/80 mt-0.5">
                    These were saved to your Trail or commented on by you while they were alive.
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={dismissAlerts}
                className="border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/20"
                data-testid="dismiss-alerts-btn"
              >
                Mark all read
              </Button>
            </div>
            <ul className="mt-3 space-y-1.5">
              {alerts.slice(0, 5).map((a) => (
                <li
                  key={`${a.user_id}-${a.video_id}`}
                  className="text-sm text-cyan-100 flex items-center gap-2 flex-wrap"
                  data-testid={`alert-row-${a.video_id}`}
                >
                  <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
                  <span className="font-medium">{a.video_title}</span>
                  {a.video_location?.city && (
                    <span className="text-cyan-300/80 text-xs">
                      · {a.video_location.city}
                      {a.video_location.country ? `, ${a.video_location.country}` : ''}
                    </span>
                  )}
                  {a.suggested_channel_username && (
                    <span className="text-[11px] text-cyan-200/70 italic">
                      could fit your @{a.suggested_channel_username} channel
                    </span>
                  )}
                  <span className="text-[11px] text-amber-300 font-mono ml-auto">
                    {a.price_credits.toFixed(2)} cr
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <ShoppingCart className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No expired videos on the market right now. Check back later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {listings.map((v) => (
              <Card
                key={v.id}
                className="bg-gray-900 border-gray-800 overflow-hidden flex flex-col"
                data-testid={`expired-listing-${v.id}`}
              >
                <div className="relative aspect-video bg-black">
                  {v.thumbnail_url ? (
                    <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" />
                  ) : v.video_url ? (
                    <video src={v.video_url} className="w-full h-full object-cover" muted />
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-700">
                      <ShoppingCart className="h-10 w-10" />
                    </div>
                  )}
                  <span className="absolute top-2 right-2 bg-amber-500 text-gray-900 text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {v.days_left_on_market}d left
                  </span>
                </div>
                <CardContent className="p-4 flex-1 flex flex-col gap-2">
                  <h3 className="font-semibold text-white truncate" title={v.title}>{v.title}</h3>
                  {v.location?.city && (
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {v.location.city}{v.location.country ? `, ${v.location.country}` : ''}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    Originally by <span className="text-gray-300">@{v.producer_username}</span>
                    {v.applaud_count > 0 && (
                      <> · {v.applaud_count} applauds</>
                    )}
                  </p>
                  <div className="mt-auto flex items-center justify-between pt-2">
                    <span className="text-amber-300 font-bold text-lg flex items-center gap-1">
                      <Coins className="h-4 w-4" />
                      {v.price_credits.toFixed(2)}
                    </span>
                    <Button
                      size="sm"
                      onClick={() => handleBuy(v)}
                      disabled={buying === v.id}
                      className="bg-emerald-600 hover:bg-emerald-500"
                      data-testid={`buy-listing-${v.id}`}
                    >
                      {buying === v.id ? 'Buying…' : 'Buy'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpiredMarketPage;
